import { Injectable } from '@angular/core';
import { EModuleIDs } from 'app/consts/enums';
import { IModule } from 'app/models/interfaces';
import { SmartMoveNavigationGroup } from '../models/smartmove-shell.models';

const GROUPS: Array<{
  id: string;
  title: string;
  icon: string;
  moduleIds?: Array<EModuleIDs>;
  standalone?: boolean;
}> = [
  {
    id: 'command-centre',
    title: 'Command Centre',
    icon: 'dashboard',
    moduleIds: [EModuleIDs.Dashboard],
  },
  {
    id: 'work',
    title: 'Work',
    icon: 'assignment',
    moduleIds: [
      EModuleIDs.Orders,
      EModuleIDs.Manifests,
      EModuleIDs.Vehicles,
      EModuleIDs.Drivers,
    ],
  },
  {
    id: 'fleet',
    title: 'Fleet',
    icon: 'local_shipping',
    moduleIds: [EModuleIDs.ManageQrCodes],
  },
  {
    id: 'customers',
    title: 'Customers',
    icon: 'groups',
    moduleIds: [EModuleIDs.Customers],
  },
  {
    id: 'safety',
    title: 'Prestart Management',
    icon: 'health_and_safety',
    moduleIds: [
      EModuleIDs.SafetyReport,
      EModuleIDs.SafetyPlans,
      EModuleIDs.SafetySettings,
    ],
  },
  {
    id: 'insights',
    title: 'Insights',
    icon: 'insights',
    moduleIds: [EModuleIDs.Reports, EModuleIDs.Analytics, EModuleIDs.AllFeedback],
  },
  {
    id: 'plans',
    title: 'Plans',
    icon: 'monetization_on',
    moduleIds: [EModuleIDs.WorkspacePlans],
  },
  {
    id: 'admin',
    title: 'Admin',
    icon: 'settings',
  },
];

@Injectable({ providedIn: 'root' })
export class SmartMoveNavigationService {
  groupModules(modules: Array<IModule>): Array<SmartMoveNavigationGroup | IModule> {
    const visible = modules.filter(module => module.visible !== false);
    const usedIds = new Set<string>();

    const groups = GROUPS
      .map((group, index): SmartMoveNavigationGroup | IModule | null => {
        if (group.id === 'safety') {
          const prestartManagement = this.findPrestartManagementModule(visible);

          if (prestartManagement) {
            const children = prestartManagement.children || [];

            usedIds.add(String(prestartManagement.id));
            children.forEach(child => usedIds.add(String(child.id)));

            return {
              ...prestartManagement,
              id: 'prestart-management',
              idx: index,
              title: group.title,
              icon: group.icon,
              type: 'collapse',
              visible: true,
              readonly: children.every(child => child.readonly),
              items: children,
              children,
            };
          }
        }

        const items =
          group.id === 'admin'
            ? this.buildAdminItems(visible, usedIds)
            : (group.moduleIds || [])
                .map(moduleId => visible.find(module => module.id === moduleId))
                .filter((module): module is IModule => Boolean(module));

        items.forEach(item => usedIds.add(String(item.id)));

        if (items.length === 0) {
          return null;
        }

        if (group.standalone) {
          const [item] = items;
          return {
            ...item,
            idx: index,
            title: group.title,
            icon: group.icon,
            type: 'button',
          };
        }

        return {
          id: group.id,
          idx: index,
          title: group.title,
          icon: group.icon,
          type: 'collapse',
          visible: true,
          readonly: items.every(item => item.readonly),
          items,
          children: items,
        };
      })
      .filter((group): group is SmartMoveNavigationGroup | IModule =>
        Boolean(group)
      );

    const ungrouped = visible.filter(module => !usedIds.has(String(module.id)));
    const bottomGroups = groups.filter(group => group.id === 'admin');
    const primaryGroups = groups.filter(group => group.id !== 'admin');

    return [...primaryGroups, ...ungrouped, ...bottomGroups];
  }

  private buildAdminItems(
    visible: Array<IModule>,
    usedIds: Set<string>
  ): Array<IModule> {
    const accounts = visible.find(module => module.id === EModuleIDs.Accounts);
    const users = visible.find(module => module.id === EModuleIDs.Users);
    const accountUsers = accounts || users;

    if (accounts) {
      usedIds.add(String(accounts.id));
    }

    if (users) {
      usedIds.add(String(users.id));
    }

    return [
      accountUsers
        ? {
            ...accountUsers,
            id: accounts ? EModuleIDs.Accounts : EModuleIDs.Users,
            title: 'Accounts / Users',
            icon: 'manage_accounts',
          }
        : null,
      visible.find(module => module.id === EModuleIDs.Timetables),
      visible.find(module => module.id === EModuleIDs.Zones),
      visible.find(module => module.id === EModuleIDs.Settings),
    ].filter((module): module is IModule => Boolean(module));
  }

  private findPrestartManagementModule(visible: Array<IModule>): IModule | null {
    return (
      visible.find(module => {
        const childIds = (module.children || []).map(child => child.id);

        return (
          module.title === 'Prestart Management' &&
          module.type === 'collapse' &&
          childIds.includes(EModuleIDs.SafetyReport)
        );
      }) || null
    );
  }
}
